"use client";

import {
  getPasswordStrength,
  sanitizeEmail,
  sanitizeName,
  isValidEmail as validateEmail,
} from "@health/validation";
import { useCallback, useState } from "react";
import { useCompanionHost, useResolvedHostUrl } from "../../../host";

export type CaptchaState = {
  nonce: string;
  image?: string;
  audio?: string;
};

export function useAuthSignup(defaultValues?: {
  firstName?: string;
  lastName?: string;
  email?: string;
}) {
  const { onAuthChange, siteId } = useCompanionHost();
  const signUpUrl = useResolvedHostUrl("/v1/auth/signup");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [firstName, setFirstName] = useState(defaultValues?.firstName || "");
  const [lastName, setLastName] = useState(defaultValues?.lastName || "");
  const [email, setEmail] = useState(defaultValues?.email || "");
  const [password, setPassword] = useState("");

  const [captcha, setCaptcha] = useState<CaptchaState | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const resetCaptcha = useCallback(() => {
    setCaptcha(null);
    setCaptchaAnswer("");
  }, []);

  const submit = async (): Promise<{ success: boolean; user?: unknown }> => {
    setError("");

    const sanitizedFirstName = sanitizeName(firstName);
    const sanitizedLastName = sanitizeName(lastName);
    const sanitizedEmail = sanitizeEmail(email);

    if (!sanitizedFirstName) {
      setError("Please enter your first name");
      return { success: false };
    }

    if (!sanitizedLastName) {
      setError("Please enter your last name");
      return { success: false };
    }

    if (!validateEmail(sanitizedEmail)) {
      setError("Please enter a valid email address");
      return { success: false };
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return { success: false };
    }

    const strength = getPasswordStrength(password);
    if (strength.strength < 2) {
      setError("Please choose a stronger password");
      return { success: false };
    }

    if (captcha && !captchaAnswer.trim()) {
      setError("Please enter the captcha");
      return { success: false };
    }

    setIsLoading(true);
    try {
      const anonymousId =
        typeof window !== "undefined" ? localStorage.getItem("chatAnonymousId") : null;

      const response = await fetch(signUpUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: sanitizedFirstName,
          lastName: sanitizedLastName,
          email: sanitizedEmail,
          password,
          ...(siteId && { siteId }),
          ...(anonymousId && { anonymousId }),
          ...(captcha && { nonce: captcha.nonce, captcha: captchaAnswer.trim() }),
        }),
        credentials: "include",
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.message || "Sign up failed. Please try again.");
        resetCaptcha();
        setIsLoading(false);
        return { success: false };
      }

      if (data.captchaRequired) {
        setCaptcha({
          nonce: data.nonce,
          image: data.captcha?.image,
          audio: data.captcha?.audio,
        });
        setCaptchaAnswer("");
        setIsLoading(false);
        return { success: false };
      }

      onAuthChange?.();
      resetCaptcha();
      setIsLoading(false);
      return { success: true, user: data.user };
    } catch (err) {
      console.error("Sign-up error:", err);
      setError("Unable to connect. Please check your internet connection and try again.");
      resetCaptcha();
      setIsLoading(false);
      return { success: false };
    }
  };

  // ask for name only if either first or last name not already available
  const showNameFields = !defaultValues?.firstName || !defaultValues?.lastName;

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    password,
    setPassword,
    captcha,
    captchaAnswer,
    setCaptchaAnswer,
    resetCaptcha,
    isLoading,
    error,
    setError,
    submit,
    showNameFields,
  };
}
